import type {
    CartFragment,
    GetActiveOrderQuery,
    GetOrderByCodeQuery,
    Order,
    OrderAddress,
} from "../common/generated-types";

export type ActiveOrderSnapshot = NonNullable<
    GetActiveOrderQuery["activeOrder"]
> & {
    shippingAddress?: OrderAddress;
    billingAddress?: OrderAddress;
};

export type OrderLine = NonNullable<
    NonNullable<GetOrderByCodeQuery["orderByCode"]>["lines"]
>[number];

export type ConfirmationSellerOrder = {
    id: string;
    code: string;
    customFields?: { isMain?: boolean | null } | null;
    lines?: Array<{
        id: string;
        productVariant?: {
            id?: string;
            pricing?: { id?: string | number } | null;
        };
    }>;
};

export type ConfirmationOrder = NonNullable<
    GetOrderByCodeQuery["orderByCode"]
> & {
    aggregateOrderId?: string | null;
    aggregateOrder?: { code?: string | null } | null;
    customFields?: { isMain?: boolean | null; isConsolidatedFeed?: boolean | null } | null;
    sellerOrders?: ConfirmationSellerOrder[] | null;
};

export interface SupplierGroup {
    supplierId: string;
    supplierName: string;
    channelOrderCode: string | null;
    lines: OrderLine[];
    totalValue: number;
    itemCount: number;
}


export type ConfirmationOrderLineProductVariant = NonNullable<
    OrderLine["productVariant"]
> & {
    pricing?: {
        id: string;
    } | null;
};

type CartFragmentOrderLine = NonNullable<CartFragment["lines"]>[number];

export type CartFragmentProductVariantPricing = {
    id: string;
    mov?: number;
    supplyType?: string;
    shippingDate?: string;
    preOrderShippingDate?: string;
    promotionDescription?: string;
    promotionTextColor?: string;
    leadTimeFrom?: number;
    leadTimeTo?: number;
    prePaymentPercentage?: number;
    fullPackageOnly?: boolean;
};

export type CartRichProductVariant = NonNullable<
    CartFragmentOrderLine["productVariant"]
> & {
    sku?: string;
    preOrderShippingDate?: string;
    pricing?: CartFragmentProductVariantPricing;
    facetValues?: ReadonlyArray<{
        facet: { code: string };
        code: string;
        name: string;
    }>;
    stockLevels?: ReadonlyArray<{
        stockOnHand: number;
        stockAllocated?: number;
    }>;
};

export type CheckoutPaymentOrderLine = CartFragmentOrderLine & {
    productVariant: CartRichProductVariant;
};

export interface CheckoutPaymentSupplierGroup {
    supplierId: string;
    supplierName: string;
    lines: CheckoutPaymentOrderLine[];
    totalValue: number;
    shippingDate: string;
    preOrderShippingDate: string;
    supplyType: string;
}

export interface BillingAddressView {
    fullName?: string;
    company?: string;
    streetLine1?: string;
    streetLine2?: string;
    city?: string;
    province?: string;
    postalCode?: string;
    countryLabel: string;
}

export type GetStripePublishableKeyQuery = {
    stripePublishableKey?: string;
};

export type GetStripeSurchargePercentQuery = {
    stripeSurchargePercent: number;
};

export type SetOrderBillingAddressMutationResult = {
    setOrderBillingAddress: Order | { __typename: string };
};

export type BillingCountryAddressLike = {
    country?: string | { name?: string };
};

export type CreateStripePaymentIntentMutationResult = {
    createStripePaymentIntent: string;
};

const SHIPPING_SURCHARGE_SKU_PREFIX = "COUNTRY_SHIPPING:";

type ShippingSurchargeOrder = {
    surcharges?: Array<{
        sku?: string | null;
        description?: string | null;
        priceWithTax?: number | null;
    }> | null;
};

function shippingSurchargeRows(
    order: object | null | undefined,
): NonNullable<ShippingSurchargeOrder["surcharges"]> {
    const rows = (order as ShippingSurchargeOrder | null | undefined)?.surcharges;
    return rows ?? [];
}

export function shippingSurchargeAmount(
    order: object | null | undefined,
): number {
    return shippingSurchargeRows(order)
        .filter((row) => row.sku?.startsWith(SHIPPING_SURCHARGE_SKU_PREFIX))
        .reduce((sum, row) => sum + Number(row.priceWithTax ?? 0), 0);
}

export function shippingSurchargeLabel(
    order: object | null | undefined,
): string {
    const row = shippingSurchargeRows(order).find((item) =>
        item.sku?.startsWith(SHIPPING_SURCHARGE_SKU_PREFIX),
    );
    return row?.description?.trim() || "Shipping with Protection";
}
